/**
 * The container board: header tally, labeled pipe-grid columns, dnd-kit
 * reorder, per-row detail strips. With a campaign GROUP open only its rows
 * show (times come from each campaign group's chain — "after previous" is
 * the previous container of the same group), a reorder moves them inside
 * their own slots of the plan order, and
 * each row names its campaign when the group has several. The head's 🌙
 * chip lists only the containers with drops outside the window (18/10/2026).
 * Until the day plan answers, a sweeping bar says it is loading and the plan
 * cells are placeholders; a failed plan says so, with Retry (23/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useMemo, useState } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Card } from '@/components/ui';
import { useBinomDragSensors } from '@/features/flow-optimizer/tabs/standard/binom/workspace/list/use-binom-drag-sensors';

import type { DayPlanPreview, DropGenPlanDetail } from '../../drop-generator.types';
import { describeContainerDrops } from '../../utils/drops/describe-drops';
import { containersCut } from '../../utils/drops/outside-window';
import { useSchedulerStatus } from '../../useDropGenerator';
import { computeContainerTimes, planWindow } from '../../utils/slot-times';
import { liveAutoSend } from '../board-header/autosend/groups/group-rows';
import type { GroupIndex } from '../campaign/group-index';
import { useBoardActions } from './actions/use-board-actions';
import { BoardEmptyState } from './BoardEmptyState';
import { ContainerRow } from './ContainerRow';
import { BoardPreviewContext, previewStateOf } from './day-plan/board-preview-state';
import { BoardPreviewBar } from './day-plan/BoardPreviewBar';
import { BoardColumnsHead } from './header/BoardColumnsHead';
import { capCutsOf } from '../shared/cap/cap-cuts';
import { BoardCardHeader } from './header/BoardCardHeader';
import { SwitchAll } from './header/SwitchAll';
import { OutsideFilterChip } from './outside/OutsideFilterChip';
import { boardTotals } from './totals/board-totals';
import { BoardTotalsRow } from './totals/BoardTotalsRow';
import { useContainerReorder } from './useContainerReorder';

interface Props {
  plan: DropGenPlanDetail;
  preview: DayPlanPreview | undefined;
  /** 23/10/2026 — the day plan is on its way / did not load (no plan to show yet). */
  previewLoading?: boolean;
  previewFailed?: boolean;
  onRetryPreview?: () => void;
  /** The open campaign group's campaigns; null = every group. */
  scope: { memberIds: ReadonlySet<number>; multi: boolean } | null;
  ix: GroupIndex;
  /** Why Add container is off for the open campaign; null = available. */
  addBlocked: string | null;
  /** The open campaign's master config control, in the card header. */
  headerExtra?: ReactNode;
  onEdit: (containerId: string) => void;
  onAdd: () => void;
  routeTestBlocked?: string | null;
  onAddRouteTest?: () => void;
}

export const BoardCard: FC<Props> = ({
  plan,
  preview,
  previewLoading = false,
  previewFailed = false,
  onRetryPreview,
  scope,
  ix,
  addBlocked,
  headerExtra,
  onEdit,
  onAdd,
  routeTestBlocked,
  onAddRouteTest,
}) => {
  const previewByContainer = new Map((preview?.containers ?? []).map((c) => [c.containerId, c]));
  const sensors = useBinomDragSensors();
  const { order, visible, onDragEnd } = useContainerReorder(plan.id, plan.containers, scope?.memberIds ?? null);
  const actions = useBoardActions();
  const masters = plan.masters ?? [];
  const showCampaign = scope ? scope.multi : new Set(order.map((c) => c.campaignWiserId)).size > 1;
  // The Danish clock (15/10/2026): times land on the preview's run date, like the planner's.
  const runDate = preview?.runDate ?? null;
  const times = useMemo(() => computeContainerTimes(order, planWindow(plan, runDate)), [order, plan, runDate]);
  const status = useSchedulerStatus().data?.plans.find((p) => p.countryKey === plan.countryKey);
  const autoSendOf = liveAutoSend(plan, status);
  const [onlyOutside, setOnlyOutside] = useState(false);
  const cutIds = new Set(containersCut(visible, times).map((c) => c.id));
  const rows = onlyOutside ? visible.filter((c) => cutIds.has(c.id)) : visible;
  const planState = previewStateOf(preview !== undefined, previewLoading, previewFailed);

  return (
    <Card className="overflow-hidden">
      <BoardCardHeader
        scoped={scope !== null}
        shown={visible.length}
        enabled={visible.filter((c) => c.enabled).length}
        extra={headerExtra}
        addBlocked={addBlocked}
        onAdd={onAdd}
        outside={<OutsideFilterChip count={cutIds.size} active={onlyOutside} onToggle={() => setOnlyOutside((v) => !v)} />}
        routeTestBlocked={routeTestBlocked}
        onAddRouteTest={onAddRouteTest}
        switchAll={visible.length > 1 ? <SwitchAll planId={plan.id} containers={visible} autoSendOf={autoSendOf} /> : null}
      />
      {visible.length === 0 ? (
        <BoardEmptyState onAdd={onAdd} campaignName={scope ? 'this campaign group' : null} addBlocked={addBlocked} />
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[1250px]">
            <BoardColumnsHead />
            <BoardPreviewBar state={planState} onRetry={onRetryPreview} />
            <BoardPreviewContext.Provider value={planState}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={rows.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                  {rows.map((container) => (
                    <ContainerRow
                      key={container.id}
                      container={container}
                      times={times.get(container.id)}
                      recipientTimezone={plan.recipientTimezone}
                      preview={previewByContainer.get(container.id)}
                      cuts={capCutsOf(preview?.warnings, container.id)}
                      autoSend={autoSendOf(container.id)}
                      runDate={preview?.runDate ?? null}
                      campaignLabel={showCampaign ? ix.nameOf(container.campaignWiserId) : undefined}
                      detail={{
                        dropsSpec: describeContainerDrops(container),
                        campaignName: ix.nameOf(container.campaignWiserId),
                        master: masters.find((m) => m.id === container.masterId) ?? null,
                      }}
                      onEdit={() => onEdit(container.id)}
                      onDuplicate={() => actions.duplicate(container.id)}
                      duplicating={actions.duplicating}
                      onDelete={() => actions.remove(container.id, container.name)}
                      deleting={actions.removing}
                      onToggleEnabled={(enabled) => actions.setContainerEnabled.mutate({ id: container.id, enabled })}
                      dragDisabled={onlyOutside}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <BoardTotalsRow totals={boardTotals(rows, (id) => previewByContainer.get(id))} />
            </BoardPreviewContext.Provider>
          </div>
        </div>
      )}
    </Card>
  );
};
